import { apiRun } from '@/model/apiRun';
import popupModel from '@/model/popup';

import CrmRejectionReasonPopupWrappable from '@/components/crm/CrmRejectionReasonPopupWrappable.vue';

import companyApi from '@/api/company';
import crmFunnelApi from '@/api/crmFunnel';
import crmFunnelMemberApi from '@/api/crmFunnelMember';
import crmLeadApi from '@/api/crmLead';
import crmLeadChangeApi from '@/api/crmLeadChange';
import crmRejectionReasonApi from '@/api/crmRejectionReason';
import crmStageApi from '@/api/crmStage';
import projectApi from '@/api/project';

/**
 * Здесь лежит только то, что переживает один экран: правила сущностей CRM,
 * общие для нескольких вызывающих выборки и сквозная политика ошибок.
 * Оркестрация конкретного экрана — что грузить при открытии, какой попап
 * открыть, как править список — живёт в соответствующем *Api.vue.
 */

/** Id из IRI: '/api/crm_stages/7' -> 7. */
export function iriId(iri) {
  if (!iri) return null;
  const id = String(typeof iri === 'object' ? iri['@id'] ?? '' : iri).split('/').pop();
  return Number.isNaN(Number(id)) ? null : Number(id);
}

/**
 * IRI связи независимо от того, как её отдал сервер.
 *
 * Одна и та же связь приходит то голой строкой, то развёрнутым объектом —
 * у заявки это вообще зависит от эндпоинта (см. «ОСОБЫЙ СЛУЧАЙ — заявки» в
 * описании API), поэтому сравнивать поле связи напрямую нельзя нигде.
 */
export function iriOf(value) {
  if (!value) return null;
  return typeof value === 'string' ? value : value['@id'] ?? null;
}

/**
 * Закрывающих этапов в API нет: у CrmStage нет признака закрытия, закрытие
 * сделки — это isSuccessful у самой заявки. На доске они живут отдельными
 * колонками поверх этапов воронки и по умолчанию не показываются.
 */
export const CLOSING_COLUMNS = [
  { id: 'won', name: 'Сделка завершена', color: '#5AC169', closing: true, isSuccessful: true },
  { id: 'lost', name: 'Отказ', color: '#E21313', closing: true, isSuccessful: false },
];

/**
 * Сортировки доски. Ключ — значение селекта «Сортировать по», подписи к нему
 * живут в вёрстке (CrmBoardUi): здесь только правило сравнения.
 */
export const LEAD_SORTS = {
  // по дате создания, новые сверху
  created: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  client: (a, b) => (a.client?.name ?? '').localeCompare(b.client?.name ?? ''),
  title: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
};

/** Кандидаты в авторы клиента по id компании, см. clientOwners. */
const clientOwnersCache = new Map();

/**
 * Сервер не даёт удалить этап, причину отказа, тип услуги и воронку, пока к
 * ним привязана хоть одна заявка: отвечает 422 и коротким английским
 * пояснением вида «Unable to delete stage with leads».
 */
const DELETE_BLOCKED_STATUS = 422;

/**
 * Сортирует элементы по массиву id (funnel.stagesOrder / funnel.reasonsOrder).
 * Бэк не валидирует состав этих массивов, поэтому всё, чего в них нет,
 * уезжает в конец списка с сохранением исходного порядка.
 */
function applyOrder(items, order) {
  if (!Array.isArray(order) || !order.length) return items;

  const index = new Map(order.map((id, i) => [id, i]));
  const at = id => index.get(id) ?? Number.MAX_SAFE_INTEGER;

  return [...items].sort((a, b) => at(a.id) - at(b.id));
}

export default {
  /**
   * Выполняет запрос и показывает алерт, если он не прошёл (см. model/apiRun.js).
   *
   * pageLevel: true передают первичным загрузкам экрана — тогда 403/404
   * остаётся в store и страница подменяется на ErrorPage. Для всего остального
   * ошибка считается обработанной здесь: показали алерт и работаем дальше.
   */
  run(action, errorText, options = {}) {
    return apiRun(action, errorText, options);
  },

  /**
   * Удаление сущности воронки с подменой пояснения к отказу.
   *
   * Пока к этапу, причине отказа, типу услуги или воронке привязана хоть одна
   * заявка, сервер отвечает 422 (см. DELETE_BLOCKED_STATUS). Его собственное
   * пояснение английское и годится только для логов, поэтому на этом статусе
   * подставляем blockedText — его и покажет алерт из run(). Остальные ошибки
   * уходят наверх как есть.
   */
  deleteWithLeadsCheck(action, blockedText) {
    return action().catch((error) => {
      if (error?.status !== DELETE_BLOCKED_STATUS) throw error;

      throw { ...error, detail: blockedText, description: blockedText };
    });
  },

  // --- Права ---------------------------------------------------------------
  //
  // Отдельного справочника ролей в CRM нет: всё считается по месту
  // пользователя в компании и составу воронки.

  /**
   * Владелец компании и заместитель — в CRM это одна роль, различий между ними
   * нет нигде. Только они управляют воронками (создание, настройка этапов и
   * причин отказа, состав участников), правят и удаляют клиентов и удаляют
   * чужие комментарии.
   */
  isCompanyManager(company, user) {
    const userId = user?.id;
    if (!userId || !company) return false;

    return company.owner?.id === userId
      || (company.deputies ?? []).some(deputy => deputy.user?.id === userId);
  },

  /**
   * Доступ к CRM компании есть у владельца, заместителей и участников любой
   * её воронки. members — результат fetchCompanyMembers: коллекция отдаёт
   * только то, что видно текущему пользователю, поэтому участнику воронки
   * приходит его собственная запись, а сотруднику вне воронок — пустой список.
   */
  canAccess(company, user, members = []) {
    return this.isCompanyManager(company, user)
      || members.some(member => member.employee?.user?.id === user?.id);
  },

  /**
   * Клиента правит руководящий состав компании и тот, кто его завёл:
   * участнику воронки чужой клиент недоступен на изменение (п. 2.5).
   *
   * isManager передают отдельно, а не считают здесь по компании: карточке
   * заявки компания не нужна больше ни для чего, она получает готовый признак
   * пропсом (canManage) — и он посчитан этим же isCompanyManager страницей.
   */
  canEditClient(client, { isManager = false, user = null } = {}) {
    if (isManager) return true;

    const ownerIri = iriOf(client?.owner);
    return !!ownerIri && ownerIri === user?.['@id'];
  },

  /**
   * Воронка целиком: её зовут и страница, и форма настройки, и попап
   * участников. withPreloader выключают там, где о ходе запроса уже сообщает
   * собственный индикатор — иначе поверх него встаёт ещё и глобальный.
   */
  fetchFunnel(id, withPreloader = true) {
    return crmFunnelApi.fetchItem(id, {}, withPreloader);
  },

  /**
   * Этапы, причины отказа и участники берутся из самой воронки: GET
   * /api/crm_funnels/{id} отдаёт их вложенными и полностью развёрнутыми,
   * отдельные запросы к /api/crm_stages, /api/crm_rejection_reasons и
   * /api/crm_funnel_members для этого не нужны.
   */
  funnelStages(funnel) {
    return applyOrder(funnel?.stages ?? [], funnel?.stagesOrder);
  },

  funnelReasons(funnel) {
    return applyOrder(funnel?.reasons ?? [], funnel?.reasonsOrder);
  },

  funnelServiceTypes(funnel) {
    return applyOrder(funnel?.serviceTypes ?? [], funnel?.serviceTypesOrder);
  },

  funnelMembers(funnel) {
    return funnel?.members ?? [];
  },

  /** Колонки доски: этапы воронки по порядку плюс две закрывающие. */
  boardColumns(stages = []) {
    const stageColumns = stages.map(stage => ({
      id: stage.id,
      name: stage.name,
      color: stage.color,
      closing: false,
      iri: crmStageApi.getIri(stage.id),
    }));

    return [...stageColumns, ...CLOSING_COLUMNS];
  },

  /**
   * Кого можно назначить ответственным по заявке: участники воронки плюс
   * владелец компании и его заместители — последние работают с любой воронкой
   * своей компании, не будучи её участниками.
   */
  assignableUsers(company, funnel) {
    return this.crmUsers(company, this.funnelMembers(funnel));
  },

  /**
   * Участники всех воронок компании одним запросом: у коллекции участников
   * есть фильтр funnel.company. Перебирать воронки item-запросами ради их
   * состава не нужно — воронок в компании может быть сколько угодно.
   */
  async fetchCompanyMembers(companyId) {
    const { items } = await crmFunnelMemberApi.fetchList({ 'funnel.company': companyId }, true);
    return items;
  },

  /**
   * Все, у кого есть доступ к CRM компании: владелец, заместители и
   * переданные участники воронок.
   *
   * Отсюда берутся кандидаты в авторы клиента: сервер принимает в авторы
   * только пользователя с доступом к компании (422 «Owner has no access to
   * the client's company»).
   */
  crmUsers(company, members = []) {
    const users = [
      company?.owner,
      ...(company?.deputies ?? []).map(deputy => deputy.user),
      ...members.map(member => member.employee?.user),
    ].filter(user => user?.['@id']);

    return [...new Map(users.map(user => [user['@id'], user])).values()];
  },

  /**
   * Кандидаты в авторы клиента (п. 3.16) — тот же список, что и crmUsers, но
   * загруженный сам.
   *
   * Нужен в двух далёких друг от друга местах: в форме нового клиента на доске
   * и в правке клиента из карточки заявки. Поэтому живёт здесь, а не грузится
   * на доске и не протаскивается пропами через попап — состав меняется редко,
   * так что держим его в кэше на компанию.
   *
   * Кэшируем промис, а не результат: два обращения подряд (форма заявки и
   * открытая следом карточка) должны разделить один запрос, а не отправить два.
   * Неудачу не кэшируем — иначе после первой же ошибки список остался бы
   * пустым до перезагрузки страницы.
   */
  clientOwners(companyId) {
    const key = String(companyId);
    if (clientOwnersCache.has(key)) return clientOwnersCache.get(key);

    const promise = this.run(
      () => Promise.all([
        companyApi.fetchItem(companyId),
        this.fetchCompanyMembers(companyId),
      ]),
      'Не удалось загрузить сотрудников компании',
    ).then((data) => {
      if (!data) {
        clientOwnersCache.delete(key);
        return [];
      }

      const [company, members] = data;
      return this.crmUsers(company, members);
    });

    clientOwnersCache.set(key, promise);
    return promise;
  },

  /** Состав воронок изменился — кандидатов соберём заново. */
  forgetClientOwners(companyId) {
    clientOwnersCache.delete(String(companyId));
  },

  // --- Заявки --------------------------------------------------------------

  /**
   * Приводит заявку к виду, одинаковому для всех эндпоинтов.
   *
   * Связи заявки разворачиваются по-разному в зависимости от запроса: в GET
   * одной заявки stage и manager приходят объектами, в списке и в ответах на
   * POST/PATCH — голыми IRI (см. «ОСОБЫЙ СЛУЧАЙ — заявки» в описании API).
   * Сырые поля не трогаем — карточке заявки нужны именно развёрнутые связи, —
   * а рядом кладём плоские скаляры, по которым доска фильтрует и раскладывает
   * заявки по колонкам, что бы ни вернул сервер.
   */
  normalizeLead(lead) {
    if (!lead) return lead;

    return { ...lead, managerIri: iriOf(lead.manager), stageId: iriId(lead.stage) };
  },

  /**
   * Поиск и фильтр по ответственному. Оба целиком на фронте: пагинации в CRM
   * нет, заявки воронки уже загружены все разом.
   *
   * Сравниваем managerIri, а не сырой manager: тот приходит то IRI-строкой, то
   * развёрнутым объектом — смотря каким запросом получена заявка (см.
   * normalizeLead).
   *
   * assignee — список IRI ответственных; пустой означает «любой», а не «никакой».
   * serviceType — список id типов услуги, пустой так же означает «любой».
   */
  filterLeads(leads, { search = '', assignee = [], serviceType = [] } = {}) {
    const needle = search.trim().toLowerCase();

    return leads.filter((lead) => {
      if (assignee.length && !assignee.includes(lead.managerIri)) return false;
      // serviceType сервер отдаёт развёрнутым объектом и в списке заявок, и в
      // одной заявке, так что сопоставлять IRI, как у ответственного, не нужно
      if (serviceType.length && !serviceType.includes(lead.serviceType?.id)) return false;
      if (!needle) return true;

      const haystack = [
        lead.title,
        lead.client?.name,
        lead.client?.phone,
        lead.client?.email,
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(needle);
    });
  },

  /** Копия списка в выбранном порядке; неизвестный ключ — как по умолчанию. */
  sortLeads(leads, sort) {
    return [...leads].sort(LEAD_SORTS[sort] ?? LEAD_SORTS.created);
  },

  /**
   * Все заявки воронки одним запросом: пагинация у CRM отключена, а поиск,
   * фильтрация и сортировка живут на фронте (см. filterLeads/sortLeads).
   *
   * У заявки нет своего поля funnel — фильтровать нужно через связанный этап,
   * иначе фильтр молча ничего не отберёт.
   */
  async fetchLeads(funnelId) {
    const { items } = await crmLeadApi.fetchList({ 'stage.funnel': funnelId }, true);
    return items.map(lead => this.normalizeLead(lead));
  },

  /**
   * withPreloader выключают, когда заявку перечитывают фоном и пользователь
   * этого не ждёт — например после закрытия шторки, чтобы карточка на доске
   * обновилась молча (см. CrmBoardApi#refreshLead).
   */
  async fetchLead(id, withPreloader = true) {
    return this.normalizeLead(await crmLeadApi.fetchItem(id, {}, withPreloader));
  },

  async createLead({ title, client, stage, manager }) { // eslint-disable-line object-curly-newline
    // manager не передаём вовсе, если он не выбран — тогда сервер сам
    // назначит текущего пользователя
    const params = { title, client, stage };
    if (manager) params.manager = manager;
    return this.normalizeLead(await crmLeadApi.create(params, true));
  },

  // --- Состояние заявки ---------------------------------------------------
  //
  // Три перехода, между которыми выбирает канбан при перетаскивании карточки.
  // Держатся вместе, потому что описывают одно правило: где стоит заявка и
  // закрыта ли она.

  /** Перенос карточки по канбану — это просто смена этапа. */
  async moveLead(id, stage) {
    return this.normalizeLead(await crmLeadApi.update(id, { stage }));
  },

  /**
   * Закрытие сделки. rejectionReason обязателен при isSuccessful=false и
   * должен принадлежать той же воронке; при успешном закрытии его нужно
   * гасить самим — сервер этого не делает.
   */
  async closeLead(id, { isSuccessful, rejectionReason = null }) {
    return this.normalizeLead(await crmLeadApi.update(id, {
      isSuccessful,
      rejectionReason,
      closedAt: new Date().toISOString(),
    }));
  },

  /**
   * Правка полей заявки, у которых нет собственных правил: тип услуги,
   * описание и вложения. Отдельного метода на каждое не заводим — в отличие
   * от этапа и ответственного это просто PATCH тем, что прислала форма.
   */
  async updateLeadFields(id, params) {
    return this.normalizeLead(await crmLeadApi.update(id, params));
  },

  /** Смена ответственного — сервер сам пишет об этом в историю заявки. */
  async updateLeadManager(id, manager) {
    return this.normalizeLead(await crmLeadApi.update(id, { manager }));
  },

  /** Возврат заявки в работу из закрывающей колонки. */
  async reopenLead(id, stage) {
    return this.normalizeLead(await crmLeadApi.update(id, {
      stage,
      isSuccessful: null,
      rejectionReason: null,
      closedAt: null,
    }));
  },

  /**
   * Какой станет заявка после перехода — то же самое, что уходит в запросе
   * выше, только посчитанное локально.
   *
   * Нужно доске: карточку она перекладывает сразу по отпусканию мыши, не
   * дожидаясь ответа сервера (см. CrmKanbanApi.onLeadMove). Живёт рядом с
   * запросами намеренно — набор полей у перехода один, и разъезжаться этим
   * двум описаниям нельзя.
   */
  leadAfterMove(lead, column, rejectionReason = null) {
    if (column.closing) {
      return {
        ...lead,
        isSuccessful: column.isSuccessful,
        rejectionReason,
        closedAt: new Date().toISOString(),
      };
    }

    // сброс полей закрытия — это случай reopenLead; у незакрытой заявки они и
    // так пустые, так что отдельная ветка не нужна
    return {
      ...lead,
      stage: column.iri,
      stageId: column.id,
      isSuccessful: null,
      rejectionReason: null,
      closedAt: null,
    };
  },

  /**
   * В какой колонке доски стоит заявка: закрытая — в завершающей, остальные —
   * в своём этапе. Правило одно и то же для канбана и для деталки заявки.
   */
  leadColumnKey(lead) {
    if (lead?.isSuccessful === true) return 'won';
    if (lead?.isSuccessful === false) return 'lost';
    return lead?.stageId ?? null;
  },

  /**
   * Перевод заявки в колонку доски: выбирает между тремя переходами выше по
   * самой колонке. Причину отказа спрашивать не пытается — её передают сюда
   * готовой (см. askRejectionReason), потому что спросить нужно раньше, чем
   * что-то менять. Возвращает заявку или null, если запрос не прошёл.
   */
  applyLeadColumn(lead, column, rejectionReason = null) {
    if (column.closing) {
      // наружу причина ходит объектом (так её отдаёт и сервер), а в запрос
      // уходит IRI — превращаем здесь, чтобы вызывающим об этом не думать
      const reasonIri = rejectionReason ? crmRejectionReasonApi.getIri(rejectionReason.id) : null;

      return this.run(
        () => this.closeLead(lead.id, {
          isSuccessful: column.isSuccessful,
          rejectionReason: reasonIri,
        }),
        'Не удалось закрыть заявку',
      );
    }

    // из закрывающей колонки заявка возвращается в работу: заодно
    // сбрасываем причину отказа и дату закрытия
    const wasClosed = lead.isSuccessful !== null && lead.isSuccessful !== undefined;

    return this.run(
      () => (wasClosed
        ? this.reopenLead(lead.id, column.iri)
        : this.moveLead(lead.id, column.iri)),
      'Не удалось перенести заявку',
    );
  },

  /**
   * Спрашивает причину отказа. Нужна только колонке «Отказ»: без неё бэк
   * вернёт 422. Возвращает саму причину или null, если отвечать отказались —
   * тогда заявку трогать нельзя.
   */
  async askRejectionReason(reasons = []) {
    if (!reasons.length) {
      await popupModel.addAlert(
        'В воронке не настроены причины отказа',
        'Добавьте их в настройках воронки — без причины заявку нельзя перевести в «Отказ»',
      );
      return null;
    }

    // не форма с селектом, а список: клик по пункту сразу закрывает попап
    const reason = await popupModel.add({
      wrappable: true,
      component: CrmRejectionReasonPopupWrappable,
      params: { reasons },
    });

    return reason || null;
  },

  // --- История заявки -----------------------------------------------------
  //
  // Записи о смене этапа и ответственного сервер создаёт сам при PATCH
  // заявки; вручную через API их не завести. Руками создаётся только
  // комментарий.

  /**
   * Лента событий заявки, свежие сверху.
   *
   * Единственный пагинированный список во всём CRM (по умолчанию 5 записей),
   * поэтому пагинацию гасим явно — лента показывается целиком.
   */
  async fetchLeadChanges(leadId) {
    const { items } = await crmLeadChangeApi.fetchList({ lead: leadId }, true);
    return items;
  },

  /**
   * Комментарий публикуется сразу: черновиков в CRM не делаем, поэтому
   * isDraft передаём false прямо в POST — иначе сервер создаст черновик,
   * невидимый остальным, и его пришлось бы публиковать отдельным PATCH.
   */
  createLeadComment({ lead, text, files = [] }) {
    return crmLeadChangeApi.create({ lead, text, files, isDraft: false });
  },

  /** Автору доступно в течение 24 часов после публикации, дальше 422. */
  updateLeadComment(id, text) {
    return crmLeadChangeApi.update(id, { text });
  },

  /**
   * Удаление добавляет в ленту запись type="comment-delete" — её создаёт
   * сервер, чтобы остальные видели факт удаления.
   */
  deleteLeadComment(id) {
    return crmLeadChangeApi.delete(id);
  },

  /**
   * Проект из заявки. Отдел обязателен вместе с crmLead — без него бэк
   * вернёт 422 (см. раздел 3.8 описания API).
   *
   * Ответственного ставим отдельным PATCH: на этом эндпоинте сервер всегда
   * назначает автором создателя проекта, поле author в POST не участвует.
   * Поэтому и алерты у шагов разные: если проект уже создан, а упал только
   * PATCH, сообщать «не удалось создать проект» нельзя — пользователь нажмёт
   * кнопку ещё раз и заведёт на заявку второй проект (их допускается
   * несколько, дубль сервер не отклонит).
   */
  async createProjectFromLead({ name, description, lead, companyDepartment, author }) { // eslint-disable-line object-curly-newline
    const project = await this.run(
      () => projectApi.create({
        name, description, crmLead: lead, companyDepartment,
      }, true),
      'Не удалось создать проект',
    );
    if (!project) return null;

    if (author && author !== project.author?.['@id']) {
      const updated = await this.run(
        () => projectApi.update(project.id, { author }, true),
        'Проект создан, но не удалось назначить ответственного',
      );
      if (updated) return updated;
    }

    return project;
  },
};
